/* eslint-disable max-len */
import { CircularProgress } from "@mui/material";
import { checkTransactionConfirmation, sleep } from "../../utils/methods";
import useMetaMask from "../../contexts/useMetaMask";
import { formatValue } from "../../utils/formatValue";
import { useCallback, useEffect, useState } from "react";
import { getContract, getMetaMaskSigner, getReadOnlyContract } from "../../services/contract.service";
import { describeChainError } from "../../services/challenge.service";
import { Result } from "ethers";
import { LandToken } from "../../types/LandToken";
import LandActionPopup, { PopupCTA, PopupFootnote } from "../LandPage/LandActionPopup";
// import { BorderLinearProgress } from "../LandPage";

type RewardData = {
  timeSinceAuction: number;
  easementDuration: number;
  eligibleCount: number;
  eligibleReward: number;
  redeemedReward: number;
  totalReward: number;
  totalClaimed: number;
  totalTrees: number;
  totalTreesClaimed: number;
  guardianReward: number;
  guardianTotalReward: number;
  patronTotalReward: number;
  guardianRedeemedReward: number;
  referralTotalReward: number;
  patronRedeemedReward: number;
  referralRedeemedReward: number;
};

type TreeClaimModalProps = {
  id: number;
  name?: string;
  content?: string;
  guardian: boolean;
  setId: any;
  // Called once after a successful claim so the host can refetch the figures it shows.
  refresh?: () => void;
  targetDate?: any;
  token: LandToken;
};

// `name` and `content` stay in the props (every mount passes them) but the shell paints the
// land card from `token`, so neither is read here any more.
const TreeClaimModalNew = ({ id, guardian, setId, token, refresh }: TreeClaimModalProps) => {
  const { library } = useMetaMask();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<RewardData | null>(null);
  const [message, setMessage] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [mintingState, setMintingState] = useState<"idle" | "minting" | "success">("idle");
  // Tree.paused() (V10.1): redeemReward is whenNotPaused, so the claim is disabled with a reason
  // rather than left to revert Paused(). Read-only; null while unknown (never blocks on a failed read).
  const [rewardsPaused, setRewardsPaused] = useState<boolean | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    const tree = getReadOnlyContract("tree-token");
    if (!tree) {
      return;
    }
    tree
      .paused()
      .then((v: boolean) => {
        if (!cancelled) {
          setRewardsPaused(Boolean(v));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRewardsPaused(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // // Uses svh when supported to avoid address bar/footer jumps, with vh fallback.
  // const mobileImageHeight = (typeof window !== "undefined" && (window as any).CSS && (window as any).CSS.supports && (window as any).CSS.supports("height", "1svh"))
  //   ? "clamp(120px, 28svh, 300px)"
  //   : "clamp(120px, 28vh, 300px)";

  // const RewardValues = [
  // {
  //   "Your address": <>
  //     <AddressLink sx={{ fontSize: "14px", lineBreak: "anywhere" }} address={currentAccount} type="user" long />
  //   </>
  // },
  // {
  //   "Remaining Mint ":
  //     <>
  //       {(data?.totalTrees !== undefined && data?.totalTreesClaimed !== undefined) ?

  //         (guardian ? formatValue((data?.guardianTotalReward - data?.redeemedReward), true) :
  //           formatValue((data?.patronTotalReward - data?.redeemedReward), true)
  //         )
  //         : "N/A"}
  //       < CurrencyLabel sx={{ fontSize: "14px" }} />
  //     </>
  // },
  // {
  //   "Active time": <>{targetDate ?
  //     <>
  //       <Text numeric capital contrast sx={{ fontSize: { xs: "12px", sm: "14px" } }}>
  //         <CountdownTimer
  //           sx={{ width: "100%" }}
  //           medium
  //           targetDate={targetDate}
  //         />
  //       </Text>
  //     </>
  //     : "N/A"}
  //     < CurrencyLabel sx={{fontSize:"14px"}} /></>
  // },
  //   "Daily mint rate": <>
  //     {formatValue((data?.totalReward ?? 0) / ((data?.easementDuration ?? 1) / TIME_UNIT_SECONDS), true, 3)} <CurrencyLabel sx={{ fontSize: "14px" }} /></>
  // },
  // { "Available to mint": <>  {formatValue(data?.eligibleReward ?? 0, true, 3)} <CurrencyLabel sx={{ fontSize: "14px" }} /></> },

  // ...(guardian ? [
  //   {
  //     "You receive (70%)": <>
  //       {formatValue(data?.guardianReward ?? 0, true, 3)} <CurrencyLabel sx={{ fontSize: "14px" }} />
  //     </>
  //   }
  // ] : []),
  // { "Mint capacity": <>{formatValue(data?.totalTrees ?? 0, true)} <CurrencyLabel /></> },
  // { "Minted currency": <>{formatValue(data?.totalClaimed ?? 0, true)}/{formatValue(data?.totalReward ?? 0, true)} <CurrencyLabel /></> },
  // { "Current bid": <>{formatValue(currentBid)} <CurrencyLabel currency={CURRENCY_NAME} /></> },
  // { "Highest bidder": <><AddressLink sx={{ fontSize: "18px" }} type="user" address={highestBidderAddress} links={false} /></> },

  // ];

  const handleCloseModal = () => {
    setOpen(false);
    setMintingState("idle");
    setLoading(false);
  };

  // const handleMintClick = () => {
  //   setLoading(true);
  //   setMintingState('minting');

  //   // Simulate mint process
  //   setTimeout(() => {
  //     setLoading(false);
  //     setMintingState('success');
  //     setTimeout(() => {
  //       alert("Mint successful!");
  //       handleCloseModal();
  //     }, 1000);
  //   }, 3000);
  // };

  const handleCancelMint = () => {
    setLoading(false);
    setMintingState("idle");
    setMessage("Minting cancelled by user");
  };

  useEffect(() => {
    if (id !== null && id !== undefined) {
      handleTreeRefresh();
    }
  }, [id]);

  const handleIsOpen = useCallback(() => {
    if (!open && data) {
      setData(null);
      setId(null);
    }
    setOpen(open);
  }, [open]);

  useEffect(() => {
    handleIsOpen();
  }, [handleIsOpen]);

  const handleTreeRefresh = async () => {
    setLoading(true);
    const signer = await getMetaMaskSigner();
    const treeContract = getContract("tree-token", signer);

    try {
      if (id !== null && id !== undefined && treeContract) {
        setMessage("Refreshing reward information...");

        const reward: Result = await treeContract.rewardDetails(id);
        const {
          timeSinceAuction,
          easementDuration,
          easementGuardian,
          guardianEligibleCount,
          guardianEligibleReward,
          guardianRedeemedReward,
          guardianTotalReward,
          easementPatron,
          patronEligibleCount,
          patronEligibleReward,
          patronRedeemedReward,
          patronTotalReward,
          easementReferralAddress,
          referralEligibleReward,
          referralRedeemedReward,
          referralTotalReward,
          totalTreesClaimed,
          totalTreeForEasement,
        } = reward.toObject();

        console.log("TreeClaimModalNew rewardDetails full values:", {
          timeSinceAuction: timeSinceAuction.toString(),
          easementDuration: easementDuration.toString(),
          easementGuardian,
          guardianEligibleCount: guardianEligibleCount.toString(),
          guardianEligibleReward: formatValue(guardianEligibleReward, false),
          guardianRedeemedReward: formatValue(guardianRedeemedReward, false),
          guardianTotalReward: formatValue(guardianTotalReward, false),
          easementPatron,
          patronEligibleCount: patronEligibleCount.toString(),
          patronEligibleReward: formatValue(patronEligibleReward, false),
          patronRedeemedReward: formatValue(patronRedeemedReward, false),
          patronTotalReward: formatValue(patronTotalReward, false),
          easementReferralAddress,
          referralEligibleReward: formatValue(referralEligibleReward, false),
          referralRedeemedReward: formatValue(referralRedeemedReward, false),
          referralTotalReward: formatValue(referralTotalReward, false),
          totalTreesClaimed: totalTreesClaimed.toString(),
          totalTreeForEasement: totalTreeForEasement.toString(),
        });

        const result: RewardData = {
          timeSinceAuction: formatValue(timeSinceAuction, false),
          easementDuration: formatValue(easementDuration, false),
          eligibleCount: formatValue(guardian ? guardianEligibleCount : patronEligibleCount, false),
          eligibleReward: formatValue(
            guardian ? guardianEligibleReward + referralEligibleReward : patronEligibleReward,
            false
          ),
          guardianReward: formatValue(guardianEligibleReward, false),
          redeemedReward: formatValue(guardian ? guardianRedeemedReward : patronRedeemedReward, false),
          totalReward: formatValue(guardian ? guardianTotalReward + referralTotalReward : patronTotalReward, false),
          totalClaimed: formatValue(
            guardian ? guardianRedeemedReward + referralRedeemedReward : patronRedeemedReward,
            false
          ),
          totalTrees: formatValue(totalTreeForEasement, false),
          totalTreesClaimed: formatValue(totalTreesClaimed, false),
          guardianTotalReward: formatValue(guardianTotalReward, false),
          patronTotalReward: formatValue(patronTotalReward, false),
          referralTotalReward: formatValue(referralTotalReward, false),
          guardianRedeemedReward: formatValue(guardianRedeemedReward, false),
          referralRedeemedReward: formatValue(referralRedeemedReward, false),
          patronRedeemedReward: formatValue(patronRedeemedReward, false),
        };

        console.log("TreeClaimModalNew rewardDetails full values:", {
          timeSinceAuction: timeSinceAuction.toString(),
          easementDuration: easementDuration.toString(),
          guardianEligibleCount: guardianEligibleCount.toString(),
          guardianEligibleReward: formatValue(guardianEligibleReward, false),
          guardianRedeemedReward: formatValue(guardianRedeemedReward, false),
          guardianTotalReward: formatValue(guardianTotalReward, false),
          patronEligibleCount: patronEligibleCount.toString(),
          patronEligibleReward: formatValue(patronEligibleReward, false),
          patronRedeemedReward: formatValue(patronRedeemedReward, false),
          patronTotalReward: formatValue(patronTotalReward, false),
          totalTreeForEasement: totalTreeForEasement.toString(),
          referralTotalReward: formatValue(referralTotalReward, false),
          referralRedeemedReward: formatValue(referralRedeemedReward, false),
          referralEligibleReward: formatValue(referralEligibleReward, false),
          totalTreesClaimed: totalTreesClaimed.toString(),
        });

        setData(result);

        if (!open) {
          setOpen(true);
        }
      }
    } catch (e: any) {
      console.error("Tree Reward Info Error", e.message);
      setMessage(e.reason || "Failed to retrieve reward details. Possibly reward is not set.");
    } finally {
      await sleep(1000);
      setMessage("");
      setLoading(false);
    }
  };

  const handleClaimTree = async () => {
    setLoading(true);
    setMintingState("minting");
    const signer = await getMetaMaskSigner();
    const treeContract = getContract("tree-token", signer);
    try {
      if (id !== null && id !== undefined && treeContract) {
        setMessage("Minting currency  started...");
        const res = await treeContract.redeemReward(id);

        if (!res?.hash) {
          setMessage("Minting transaction failed to complete...");
        } else {
          setMessage("Awaiting transaction to complete...");
          const result = await checkTransactionConfirmation(res.hash, library);
          console.log({ TransactionResult: result });
          await sleep(2000);
          setMessage("EFT Reward minted");
          await handleTreeRefresh();
          refresh?.();
          setMintingState("success");
          handleCloseModal();
        }
      }
    } catch (e: any) {
      console.log({ result: e });
      // Tree.redeemReward reverts Paused() while the protocol has rewards paused (V10.1), and
      // RedeemIntervalNotMet / NoEligible*Reward when there is nothing to claim yet. Say so.
      setMessage(describeChainError(e));
      await sleep(1000);
      setMessage("");
    } finally {
      setLoading(false);
    }
  };

  // TREE mint, on the land-card + purple-panel shell the offer / bid popups use (Figma node
  // 3002:4045). Rows are what the old frosted panel listed — EcoScore, what is still mintable
  // over the term, the contract countdown — and AVAILABLE MINT, the figure the CTA acts on,
  // is the headline under them. `content` (the raw land image) is no longer painted here:
  // the shell draws the ≤768px poster rendition off `token`, since the full-size GIFs stall
  // a phone at card size.
  return (
    <LandActionPopup
      open={open}
      onClose={handleCloseModal}
      token={token}
      statLabel="AVAILABLE MINT"
      statValue={data?.eligibleReward !== undefined ? formatValue(data.eligibleReward, true) + " TR3" : "N/A"}
      // rows={[
      //   { label: "EcoScore", value: token?.ecoScore ? (token.ecoScore * 100).toFixed(0) : "N/A" },
      //   {
      //     label: "Remaining mint",
      //     value:
      //       data?.guardianTotalReward !== undefined &&
      //       data?.patronTotalReward !== undefined &&
      //       data?.redeemedReward !== undefined
      //         ? guardian
      //           ? formatValue(data.guardianTotalReward - data.redeemedReward, true) + " TR3"
      //           : formatValue(data.patronTotalReward - data.redeemedReward, true) + " TR3"
      //         : "N/A",
      //   },
      //   {
      //     label: "Contract period",
      //     value: (
      //       <CountdownTimer
      //         medium
      //         sx={popupTimerSx}
      //         targetDate={moment(token?.etNft?.easementActivated?.contractStart)
      //           .add(token.meta?.contractPeriod || 1, TERM_UNIT)
      //           .toDate()}
      //       />
      //     ),
      //   },
      // ]}
    >
      {/* While redeemReward is in flight the CTA becomes the cancel: same spinner-only face
          the old MintCancelButton had, and a tap still calls handleCancelMint. */}
      {mintingState === "minting" ? (
        <PopupCTA onClick={handleCancelMint} variant="contained">
          <CircularProgress size={20} sx={{ color: "white" }} />
        </PopupCTA>
      ) : (
        <PopupCTA
          onClick={handleClaimTree}
          disabled={loading || !data?.eligibleReward || rewardsPaused === true}
          variant="contained"
        >
          {loading ? "Loading..." : rewardsPaused ? "Rewards paused" : "Mint TR3"}
        </PopupCTA>
      )}

      {rewardsPaused && (
        <PopupFootnote>
          TREE rewards are paused by the protocol right now. Your accrual is unaffected; claiming resumes when they do.
        </PopupFootnote>
      )}

      {/* The transaction narration ("Awaiting transaction to complete…") and any chain error. */}
      {message && <PopupFootnote>{message}</PopupFootnote>}
    </LandActionPopup>
  );
};

export default TreeClaimModalNew;
